import { intlLocale, isZH, type Lang } from './i18n.ts';

/**
 * 显示层的格式化。
 *
 * ════════════════════════════════════════════════════════
 * 一条贯穿全文件的规则：**金额从后端来的是「微美元的字符串」。**
 * ════════════════════════════════════════════════════════
 * 后端一律用 bigint 微美元（1 USD = 1_000_000），JSON 里传字符串 ——
 * 因为微美元的值可以超过 2^53，转 number 会静默丢精度。
 *
 * 所以这里的入口是 `string`，内部用 BigInt 算，只在**最后一步**
 * 转成给人看的字符串。中间任何一步用 number 都会把精度丢回去。
 */

const MICRO_PER_USD = 1_000_000n;

/** 微美元字符串 → 给人看的金额 */
export function money(
  micro: string | null | undefined,
  lang: Lang,
  opts: { sign?: boolean; compact?: boolean; dp?: 0 | 2 } = {}
): string {
  if (micro === null || micro === undefined || micro === '') return '—';
  let v: bigint;
  try {
    v = BigInt(micro);
  } catch {
    return '—';
  }
  const neg = v < 0n;
  const abs = neg ? -v : v;

  /* 符号用 U+2212 真减号而不是 ASCII 连字符：
     等宽数字里 ASCII 的 - 比数字窄，一列负数会参差不齐 */
  const sign = neg ? '−' : opts.sign && v > 0n ? '+' : '';

  if (opts.compact) {
    const usd = Number(abs / 10_000n) / 100; // 保留两位小数的精度，够紧凑显示用
    if (usd >= 1000) {
      const body = isZH(lang) && usdCompactCN()
        ? usd >= 1e8
          ? trimZeros((usd / 1e8).toFixed(2)) + '亿'
          : trimZeros((usd / 1e4).toFixed(1)) + '万'
        : usd >= 1e9
          ? trimZeros((usd / 1e9).toFixed(2)) + 'B'
          : usd >= 1e6
            ? trimZeros((usd / 1e6).toFixed(1)) + 'M'
            : trimZeros((usd / 1e3).toFixed(1)) + 'K';
      return sign + '$' + body;
    }
  }

  const dp = opts.dp ?? 2;
  /* 整数部分和小数部分**分开算**，全程 BigInt。
     先转 Number 再 toFixed 会在大额上丢精度 —— 而这是账目数字。 */
  const scale = dp === 0 ? MICRO_PER_USD : MICRO_PER_USD / 100n;
  const rounded = (abs + scale / 2n) / scale; // 四舍五入到目标精度
  const int = dp === 0 ? rounded : rounded / 100n;
  const frac = dp === 0 ? 0n : rounded % 100n;
  const intStr = int.toLocaleString(intlLocale(lang));
  return sign + '$' + intStr + (dp === 0 ? '' : '.' + String(frac).padStart(2, '0'));
}

/* 大数字写法跟外观里的 numfmt 维度走。这里读 <html> 上那个属性 ——
   比把它一路传参传进每个格式化调用要干净 */
function usdCompactCN(): boolean {
  try {
    return document.documentElement.dataset['numfmt'] === 'wan';
  } catch {
    return false;
  }
}

const trimZeros = (s: string): string => s.replace(/\.?0+$/, '');

/** 万分点 → 百分比 */
export function bps(v: number | null | undefined, dp = 1): string {
  if (v === null || v === undefined || !Number.isFinite(v)) return '—';
  return (v / 100).toFixed(dp) + '%';
}

/** 普通数字，带千分位 */
export function num(v: number | null | undefined, lang: Lang): string {
  if (v === null || v === undefined || !Number.isFinite(v)) return '—';
  return v.toLocaleString(intlLocale(lang));
}

/**
 * 「N 秒/分钟/小时前」。
 *
 * **这个函数是整个界面最重要的一处诚实。**
 * 一个风控工具最危险的失败不是「不工作」，而是「看起来在工作」——
 * 用户瞥一眼以为手里的盈亏是此刻的实况，然后据此决定要不要加仓。
 * 所以凡是显示数据的地方，旁边都要有它。
 */
export function ago(iso: string | number | null | undefined, now: number, lang: Lang): string {
  if (iso === null || iso === undefined || iso === '') return zh(lang, '从未', '從未', 'never');
  const t = typeof iso === 'number' ? iso : Date.parse(iso);
  if (!Number.isFinite(t) || t <= 0) return zh(lang, '从未', '從未', 'never');
  const ms = now - t;
  if (ms < 0) return zh(lang, '刚刚', '剛剛', 'just now');
  const s = Math.floor(ms / 1000);
  if (s < 10) return zh(lang, '刚刚', '剛剛', 'just now');
  if (s < 60) return s + zh(lang, ' 秒前', ' 秒前', 's ago');
  const m = Math.floor(s / 60);
  if (m < 60) return m + zh(lang, ' 分钟前', ' 分鐘前', 'm ago');
  const h = Math.floor(m / 60);
  if (h < 24) return h + zh(lang, ' 小时前', ' 小時前', 'h ago');
  const d = Math.floor(h / 24);
  return d + zh(lang, ' 天前', ' 天前', 'd ago');
}

/** 一段时长 */
export function duration(seconds: number | null | undefined, lang: Lang): string {
  if (seconds === null || seconds === undefined || !Number.isFinite(seconds) || seconds <= 0) {
    return '0' + zh(lang, ' 秒', ' 秒', 's');
  }
  const s = Math.round(seconds);
  if (s < 60) return s + zh(lang, ' 秒', ' 秒', 's');
  const m = Math.floor(s / 60);
  if (m < 60) {
    return s % 60 === 0
      ? m + zh(lang, ' 分钟', ' 分鐘', 'm')
      : m + zh(lang, ' 分 ', ' 分 ', 'm ') + (s % 60) + zh(lang, ' 秒', ' 秒', 's');
  }
  const h = Math.floor(m / 60);
  return m % 60 === 0
    ? h + zh(lang, ' 小时', ' 小時', 'h')
    : h + zh(lang, ' 小时 ', ' 小時 ', 'h ') + (m % 60) + zh(lang, ' 分', ' 分', 'm');
}

/** 绝对时刻，按用户时区显示 */
export function clock(
  iso: string | null | undefined,
  lang: Lang,
  opts: { timezone?: string; withDate?: boolean } = {}
): string {
  if (iso === null || iso === undefined || iso === '') return '—';
  const t = Date.parse(iso);
  if (!Number.isFinite(t)) return '—';
  try {
    return new Intl.DateTimeFormat(intlLocale(lang), {
      hour: '2-digit',
      minute: '2-digit',
      /* 12/24 小时制跟外观里的 clock 维度走 */
      hour12: document.documentElement.dataset['clock'] === '12h',
      ...(opts.withDate ? { month: '2-digit', day: '2-digit' } : {}),
      ...(opts.timezone ? { timeZone: opts.timezone } : {})
    }).format(new Date(t));
  } catch {
    /* 时区名不合法（用户的设置里存了一个已经被 IANA 删掉的名字）时
       不要整块报错，退回不带时区的显示 */
    return new Date(t).toISOString().slice(11, 16);
  }
}

/** 地址缩写。前 6 后 4 */
export function shortAddress(addr: string): string {
  return addr.length <= 12 ? addr : `${addr.slice(0, 6)}…${addr.slice(-4)}`;
}

function zh(lang: Lang, a: string, b: string, c: string): string {
  return lang === 'zh-CN' ? a : lang === 'zh-HK' ? b : c;
}
